import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { ControlContainer } from '@angular/forms';
import {
    DOCUMENT_FORM_ID,
    Document,
    FormSetup,
    KeyValueObject,
    LookupObject,
    LookupSourceType,
    TRANSLATION_DOCUMENT_LOOKUP_ADD_DOCUMENT,
    TRANSLATION_DOCUMENT_LOOKUP_ADD_DOCUMENT_PLURAL,
    TRANSLATION_DOCUMENT_LOOKUP_MAX_DOCUMENTS,
    UTILS_ICON_ADD,
} from '@wdx/shared/utils';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseWdxFormControlClass } from '../../../abstract-classes/base-form-control.class';
import {
    CreatedDocumentInfo,
    IFormDocumentHandler,
    IFormDynamicData,
} from '../../../interfaces';
import { FormContextualDataService } from '../../../services';

@Component({
    selector: 'wdx-ff-document-lookup-control',
    templateUrl: './form-document-lookup-control.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FormDocumentLookupControlComponent
    extends BaseWdxFormControlClass
    implements OnInit
{
    addDocumentFormSetup$ = new BehaviorSubject<FormSetup>(null as any);
    UTILS_ICON_ADD = UTILS_ICON_ADD;

    readonly DOCUMENT_FORM_ID = DOCUMENT_FORM_ID;
    readonly DOCUMENT_LOOKUP_MAX_DOCUMENTS =
        TRANSLATION_DOCUMENT_LOOKUP_MAX_DOCUMENTS;
    readonly DOCUMENT_LOOKUP_ADD_DOCUMENT =
        TRANSLATION_DOCUMENT_LOOKUP_ADD_DOCUMENT;
    readonly DOCUMENT_LOOKUP_ADD_DOCUMENT_PLURAL =
        TRANSLATION_DOCUMENT_LOOKUP_ADD_DOCUMENT_PLURAL;

    get documents(): Document[] {
        return this.formControl?.value || [];
    }

    get label() {
        return `${this.formElement?.label} (${
            this.formControl?.value?.length || 0
        })`;
    }

    constructor(
        public override controlContainer: ControlContainer,
        public override dynamicDataService: IFormDynamicData,
        private cdf: ChangeDetectorRef,
        private formContextualDataService: FormContextualDataService,
        private documentHandler: IFormDocumentHandler,
    ) {
        super(controlContainer, dynamicDataService);
    }

    ngOnInit() {
        this.addDocumentFormSetup$.next(this.getAddDocumentFormSetup());

        this.controlContainer.control?.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() =>
                this.addDocumentFormSetup$.next(this.getAddDocumentFormSetup()),
            );

        this.documentHandler
            .createDocumentSuccess$()
            .subscribe((info: CreatedDocumentInfo) => this.updateForm(info));

        this.documentHandler
            .deleteDocumentSuccess$()
            .subscribe((id) => this.removeDocumentFromForm(id));
    }

    getAddDocumentFormSetup(): FormSetup {
        return {
            isQuickCreate: true,
            preserveActiveModal: true,
            formId: DOCUMENT_FORM_ID,
            initialisationParams: {
                ...this.getInitialisationParams(),
                ...this.getSchemaInitialisationParams(),
            },
        };
    }

    getInitialisationParams(): KeyValueObject {
        const params: Record<string, string> = {};
        this.formElement?.initialisationParameters?.forEach((param) => {
            const { field, parameter, entityTypes } = param;
            if (!field || !parameter || !entityTypes?.length) {
                return;
            }

            const lookupObjects = [
                this.formStaticService.form.getRawValue()[field],
            ]
                .flatMap((x) => x)
                .filter(Boolean) as LookupObject[];

            if (!lookupObjects.length) {
                return;
            }

            const valueString = lookupObjects
                .filter((lo) => entityTypes.includes(lo.lookupType as string))
                .map((lo) => lo?.id)
                .filter(Boolean)
                .join(',');

            if (valueString) {
                params[parameter] = valueString;
            }
        });

        return params;
    }

    getSchemaInitialisationParams(): KeyValueObject {
        const documentCategory = this.formElement?.documentCategory;
        return {
            ...(documentCategory && { documentCategory }),
        };
    }

    updateForm(docInfo: CreatedDocumentInfo): void {
        const { id, name, fileIndex } = docInfo;
        const updateIndex = this.documents.findIndex((val) => val.id === id);
        const updateValue = {
            id,
            type: LookupSourceType.Document,
            name,
            fileIndex,
        };
        if (updateIndex > -1) {
            this.formControl?.setValue(
                this.documents.map((val) => {
                    if (val.id === id) {
                        return updateValue;
                    }
                    return val;
                }),
            );
        } else {
            this.formControl?.setValue([...this.documents, ...[updateValue]]);
        }
    }

    removeDocumentFromForm(id: string): void {
        this.formControl?.setValue(
            this.documents.filter((document) => document.id !== id),
        );
        this.cdf.detectChanges();
    }

    editDocument(formSetup: FormSetup) {
        this.documentHandler.editDocument(formSetup);
    }

    addDocument(formSetup: FormSetup) {
        this.documentHandler.addDocument(formSetup);
    }

    onDeleteDocument(document: Document): void {
        this.documentHandler.deleteDocument(
            document.id as string,
            document.name as string,
        );
    }
}
